import { CRISIS_PROGRAM_COURSE_ID } from "@/shared/lib/cohortCourse";

/** Courses that are not part of the public catalog and require a concrete
 * enrollment or per-user entitlement. */
const ASSIGNED_ONLY_COURSES = new Set<string>([CRISIS_PROGRAM_COURSE_ID]);

export interface CourseAccessSignals {
  assignedCourseId?: string | null;
  grantedCourseIds?: readonly string[];
  purchasedCourseIds?: readonly string[];
}

export function isAssignedOnlyCourse(courseId?: string | null): boolean {
  return !!courseId && ASSIGNED_ONLY_COURSES.has(courseId);
}

export function accessibleCourseIds(signals: CourseAccessSignals): string[] {
  return [
    ...new Set(
      [
        signals.assignedCourseId,
        ...(signals.grantedCourseIds ?? []),
        ...(signals.purchasedCourseIds ?? []),
      ].filter((id): id is string => typeof id === "string" && id.length > 0),
    ),
  ];
}

/** Public courses stay public. Assigned-only courses fail closed unless the
 * account context carries an authoritative enrollment or explicit grant. */
export function canAccessCourse(
  courseId: string,
  allowedCourseIds: readonly string[],
): boolean {
  return !isAssignedOnlyCourse(courseId) || allowedCourseIds.includes(courseId);
}

export function courseIdFromPath(pathname: string): string | null {
  const match = pathname
    .split("?")[0]
    .split("#")[0]
    .match(/^\/course\/([^/]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/** A protected route requested before sign-in is still subject to the newly
 * authenticated account's course access. */
export function authorizedReturnTarget(
  target: string | null,
  allowedCourseIds: readonly string[],
): string | null {
  if (!target) return null;
  const courseId = courseIdFromPath(target);
  return courseId && !canAccessCourse(courseId, allowedCourseIds)
    ? null
    : target;
}
